import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { ButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';

import * as libRegions from '@luxuryescapes/lib-regions';

import { LUX_PLUS } from '~/consts/membership';

import useCurrentUser from '~/hooks/useCurrentUser';

import {
  cancelPreviewPeriod,
  createCustomerSubscriptionsForLuxPlusAdmins,
  getSubscriptionOffers,
} from '~/services/MembershipService';

import { hasLuxPlusAdminAccess } from '~/utils/luxPlus';

import { getErrorMessage, validateMembershipBrand, validateMembershipRegion } from './utils';

interface Props {
  customer: App.User;
  refreshData: () => Promise<void>;
  isFullLuxPlusMember: boolean;
  isFreePreviewMember: boolean;
}

type DialogStep = 'cancel-preview' | 'upgrade' | null;

const luxPlusRoyalBlue = '#4169E1';

const RoyalBlueButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: theme.palette.getContrastText(luxPlusRoyalBlue),
  backgroundColor: luxPlusRoyalBlue,
  '&:hover': {
    backgroundColor: luxPlusRoyalBlue,
  },
}));

export default function UpgradeToLuxPlusButton({
  customer,
  refreshData,
  isFullLuxPlusMember,
  isFreePreviewMember,
}: Props) {
  const { user } = useCurrentUser();
  const tenant = useSelector((state: App.State) => state.tenant);
  const { brand } = tenant;
  const { enqueueSnackbar } = useSnackbar();

  const [isProcessing, setProcessing] = useState(false);
  const [dialogStep, setDialogStep] = useState<DialogStep>(null);

  const isCurrentUserLuxPlusAdmin = hasLuxPlusAdminAccess(user);
  if (!isCurrentUserLuxPlusAdmin) return null;
  // Hide only for paid, redeemable members. Free preview and payment-failure
  // (AWAITING_RENEWAL) still need a way to grant full LuxPlus+.
  if (isFullLuxPlusMember) return null;

  const closeDialog = () => {
    if (!isProcessing) {
      setDialogStep(null);
    }
  };

  const createLuxPlusSubscription = async ({
    region,
    subscriptionOfferId,
    price,
  }: {
    region: App.MembershipRegion;
    subscriptionOfferId: string;
    price: number;
  }) => {
    try {
      const currency = libRegions.getRegionByCode(customer.country_code)?.currencyCode;

      validateMembershipBrand(brand);

      return await createCustomerSubscriptionsForLuxPlusAdmins({
        customerId: customer.id_member,
        region,
        currency,
        price,
        subscriptionOfferId,
        brand,
      });
    } catch (e) {
      enqueueSnackbar(getErrorMessage(e), {
        variant: 'error',
      });

      return;
    }
  };

  const handleUpgradeToLuxPlus = async () => {
    setProcessing(true);
    setDialogStep(null);

    const region = customer.country_code;
    if (!region) {
      enqueueSnackbar(`Customer home region not set, please set home region before upgrading to LuxPlus+`, {
        variant: 'error',
      });
      setProcessing(false);
      return;
    }

    let subscriptionOffers;
    try {
      validateMembershipRegion(region);
      validateMembershipBrand(brand);
      subscriptionOffers = await getSubscriptionOffers({
        brand,
        region,
      });
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), {
        variant: 'error',
      });
      setProcessing(false);
      return;
    }

    const offer = subscriptionOffers.find((subscriptionOffer) => subscriptionOffer.joiningFee === 0);
    if (!offer) {
      enqueueSnackbar(`No complimentary ${LUX_PLUS.PROGRAM_NAME} offer found for this region`, {
        variant: 'error',
      });
      setProcessing(false);
      return;
    }

    let createdSubscription;
    try {
      createdSubscription = await createLuxPlusSubscription({
        region,
        subscriptionOfferId: offer.id,
        price: offer.price,
      });
    } catch (err) {
      enqueueSnackbar(`Failed to create ${LUX_PLUS.PROGRAM_NAME} subscription`, {
        variant: 'error',
      });
    }

    if (createdSubscription) {
      enqueueSnackbar(`Success. This customer is now a ${LUX_PLUS.PROGRAM_NAME} Member!`, {
        variant: 'success',
      });
      await refreshData();
    }

    setProcessing(false);
  };

  const handleClickUpgrade = () => {
    if (isFreePreviewMember) {
      setDialogStep('cancel-preview');
      return;
    }

    handleUpgradeToLuxPlus();
  };

  const handleCancelPreview = async () => {
    setProcessing(true);

    try {
      await cancelPreviewPeriod({ customerId: customer.id_member, op: 'cancel' });
      enqueueSnackbar('Free preview cancelled', { variant: 'success' });
      await refreshData();
      setDialogStep('upgrade');
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), {
        variant: 'error',
      });
      setDialogStep(null);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeclineUpgradeAfterPreviewCancel = async () => {
    setDialogStep(null);
    await refreshData();
  };

  return (
    <>
      {isProcessing && dialogStep !== 'cancel-preview' ? (
        <RoyalBlueButton variant="contained">
          Processing...
          <Box sx={{ display: 'flex' }}>
            <CircularProgress size="24px" sx={{ marginLeft: '24px', marginRight: '16px' }} />
          </Box>
        </RoyalBlueButton>
      ) : (
        <RoyalBlueButton variant="contained" onClick={handleClickUpgrade} disabled={isProcessing}>
          Free Upgrade to {LUX_PLUS.PROGRAM_NAME}
        </RoyalBlueButton>
      )}

      <Dialog
        open={dialogStep === 'cancel-preview'}
        onClose={closeDialog}
        aria-labelledby="cancel-preview-before-upgrade-title"
        aria-describedby="cancel-preview-before-upgrade-description"
      >
        <DialogTitle id="cancel-preview-before-upgrade-title">Cancel the free preview period</DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-preview-before-upgrade-description">
            This customer is on a {LUX_PLUS.PROGRAM_NAME} free preview. Full membership cannot be granted while a
            preview is active. Cancel the free preview first, then you will be asked to grant complimentary full
            membership.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeDialog} disabled={isProcessing} autoFocus>
            Don&apos;t cancel
          </Button>
          <Button variant="contained" onClick={handleCancelPreview} disabled={isProcessing}>
            {isProcessing ? 'Cancelling…' : 'Cancel preview'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogStep === 'upgrade'}
        onClose={handleDeclineUpgradeAfterPreviewCancel}
        aria-labelledby="upgrade-after-preview-title"
        aria-describedby="upgrade-after-preview-description"
      >
        <DialogTitle id="upgrade-after-preview-title">
          Grant complimentary {LUX_PLUS.PROGRAM_NAME} membership
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="upgrade-after-preview-description">
            The free preview has been cancelled. Grant complimentary full {LUX_PLUS.PROGRAM_NAME} membership? This does
            not charge the customer. If you don&apos;t upgrade, they will have no {LUX_PLUS.PROGRAM_NAME} access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleDeclineUpgradeAfterPreviewCancel} autoFocus>
            Don&apos;t upgrade
          </Button>
          <Button variant="contained" onClick={handleUpgradeToLuxPlus}>
            Upgrade to {LUX_PLUS.PROGRAM_NAME}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
